import { useBusinessEntity, useOwnership } from '@commenda/api';
import {
  CountryCode,
  FullscreenModal,
  FullscreenModalContent,
  Loading,
} from '@commenda/kakashi';
import { IncorporationFormHeader } from '../../form-builder/components/new-incorp-layout';
import { EditStakeholderForm } from './forms/EditStakeholderForm';
import { useEntityOwnershipNouns } from './ownershipNouns';

interface EditStakeholderPageProps {
  corporationId: number;
  stakeholdingId: string;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const EditStakeholderPage = ({
  corporationId,
  stakeholdingId,
  onClose,
  onSuccess,
  onError,
}: EditStakeholderPageProps) => {
  const { data: businessEntity, isLoading: isEntityLoading } =
    useBusinessEntity(corporationId);
  const { data: ownership, isLoading: isOwnershipLoading } = useOwnership(
    corporationId,
    {
      offset: 0,
      limit: 500,
    }
  );

  // Form auto-detects INDIVIDUAL vs INSTITUTION from `stakeholder.type` —
  // the route doesn't need to know up-front.
  const stakeholder = ownership?.shareholdings.find(
    (s) => s.stakeholdingId === stakeholdingId,
  );

  const nouns = useEntityOwnershipNouns(corporationId);
  const entityName = businessEntity?.generalCorporationInfo?.legalName ?? '';
  const entityCountry =
    businessEntity?.generalCorporationInfo?.incorporationCountry;
  const isLoading = isEntityLoading || isOwnershipLoading;

  return (
    <FullscreenModal isOpen onClose={onClose} enableEscapeKey>
      <IncorporationFormHeader
        entity={
          entityName && entityCountry
            ? { name: entityName, countryCode: entityCountry as CountryCode }
            : undefined
        }
        onClose={onClose}
      />

      <FullscreenModalContent className="bg-bg-light flex justify-center overflow-y-auto">
        {isLoading || !ownership ? (
          <div className="flex flex-1 items-center justify-center py-7xl">
            <Loading />
          </div>
        ) : !stakeholder ? (
          <div className="flex flex-1 items-center justify-center py-7xl">
            <p className="txt-small-regular text-text-tertiary">
              {nouns.singular} not found.
            </p>
          </div>
        ) : (
          <div className="flex w-[640px] max-w-full flex-col gap-2xl py-4xl">
            <EditStakeholderForm
              corporationId={corporationId}
              stakeholder={stakeholder}
              percentScale={ownership.percentScale}
              onCancel={onClose}
              onSuccess={onSuccess}
              onError={onError}
            />
          </div>
        )}
      </FullscreenModalContent>
    </FullscreenModal>
  );
};
